import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Users, 
  UserCheck, 
  Package, 
  ShoppingCart, 
  DollarSign,
  HeadphonesIcon,
  Bell,
  TrendingUp
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, title, value, subtitle, color = '#467322' }) => (
  <Card className="bg-white border border-[#467322]/20 hover:shadow-lg transition-all duration-200 nutrigym-card-hover">
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-600 font-medium mb-1">{title}</p>
          <p className="text-3xl font-bold text-black" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            {value}
          </p>
          {subtitle && (
            <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
          )}
        </div>
        <div 
          className="p-3 rounded-lg"
          style={{ backgroundColor: `${color}15` }}
        >
          <Icon size={24} style={{ color }} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const AdminDashboard = () => {
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#467322] border-t-transparent" />
      </div>
    );
  }

  const statsData = [
    { 
      icon: Users, 
      title: t('totalDistributors'), 
      value: stats?.total_distributors || 0,
      color: '#467322'
    },
    { 
      icon: UserCheck, 
      title: t('totalClients'), 
      value: stats?.total_clients || 0,
      color: '#6CA33E'
    },
    { 
      icon: Package, 
      title: t('totalSupplements'), 
      value: stats?.total_supplements || 0,
      color: '#467322'
    },
    { 
      icon: ShoppingCart, 
      title: t('totalOrders'), 
      value: stats?.total_orders || 0,
      color: '#3A601C'
    },
    { 
      icon: DollarSign, 
      title: t('totalRevenue'), 
      value: `$${(stats?.total_revenue || 0).toLocaleString()}`,
      color: '#467322'
    },
    { 
      icon: HeadphonesIcon, 
      title: t('pendingSupportClients'), 
      value: stats?.pending_support_clients || 0,
      color: '#EF4444'
    },
    { 
      icon: HeadphonesIcon, 
      title: t('pendingSupportDistributors'), 
      value: stats?.pending_support_distributors || 0,
      color: '#F59E0B'
    },
    { 
      icon: Bell, 
      title: t('recentNotifications'), 
      value: stats?.recent_notifications || 0,
      color: '#467322'
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-[#467322]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            {t('dashboard')}
          </h1>
          <p className="text-gray-600 mt-1">{t('overview')}</p>
        </div>
        <div className="flex items-center gap-2 text-[#467322]">
          <TrendingUp size={20} />
          <span className="text-sm font-medium">En tiempo real</span>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statsData.map((stat) => (
          <StatCard key={stat.title} {...stat} />
        ))}
      </div>

      {/* Quick Summary Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Users Summary */}
        <Card className="bg-[#467322] border-none">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              <Users size={24} />
              Resumen de Usuarios
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-white/10 rounded-lg">
                <p className="text-3xl font-bold text-white">{stats?.total_admins || 0}</p>
                <p className="text-white/80 text-sm">Admins</p>
              </div>
              <div className="text-center p-4 bg-white/10 rounded-lg">
                <p className="text-3xl font-bold text-white">{stats?.total_distributors || 0}</p>
                <p className="text-white/80 text-sm">Distribuidores</p>
              </div>
              <div className="text-center p-4 bg-white/10 rounded-lg">
                <p className="text-3xl font-bold text-white">{stats?.total_clients || 0}</p>
                <p className="text-white/80 text-sm">Clientes</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Revenue Summary */}
        <Card className="bg-white border border-[#467322]/20">
          <CardHeader>
            <CardTitle className="text-[#467322] flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              <DollarSign size={24} />
              Resumen Financiero
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-[#F2F7F0] rounded-lg">
                <span className="text-gray-700">Ingresos Totales</span>
                <span className="text-2xl font-bold text-[#467322]">
                  ${(stats?.total_revenue || 0).toLocaleString()}
                </span>
              </div>
              <div className="flex items-center justify-between p-4 bg-[#F2F7F0] rounded-lg">
                <span className="text-gray-700">Pedidos Totales</span>
                <span className="text-2xl font-bold text-[#467322]">
                  {stats?.total_orders || 0}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Support Status */}
      <Card className="bg-white border border-[#467322]/20">
        <CardHeader>
          <CardTitle className="text-[#467322] flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            <HeadphonesIcon size={24} />
            Estado del Soporte
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center justify-between p-4 border border-[#467322]/20 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <HeadphonesIcon size={20} className="text-red-500" />
                </div>
                <span className="text-gray-700">Tickets Clientes Pendientes</span>
              </div>
              <span className="text-xl font-bold text-red-500">
                {stats?.pending_support_clients || 0}
              </span>
            </div>
            <div className="flex items-center justify-between p-4 border border-[#467322]/20 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <HeadphonesIcon size={20} className="text-amber-500" />
                </div>
                <span className="text-gray-700">Tickets Distribuidores Pendientes</span>
              </div>
              <span className="text-xl font-bold text-amber-500">
                {stats?.pending_support_distributors || 0}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;
